// External
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { CommercialidsService } from 'src/app/common/services/process/commercialids/commercialIds.service';
import { ProfilesService } from 'src/app/common/services/process/profiles/profiles.service';

// Internal
import { AllocationCases, BundleModel } from '../../common/models/Services.model';
import { UsefulService } from '../../common/services/global/useful/useful.service';
import { DevicesService } from '../../common/services/process/devices/devices.service';
import { SubscriptionsService } from '../../common/services/process/subscriptions/subscriptions.service';
import { TtokenService } from '../../common/services/process/ttoken/ttoken.service';
import { SubscriptionsValuesService } from '../../common/values/subscriptions.values.service';
import { ValuesService } from '../../common/values/values.service';

@Injectable({
    providedIn: 'root'
})
export class SubscriptionsHelperService {

    constructor(
        readonly subscriptionsService: SubscriptionsService,
        readonly devicesService: DevicesService,
        readonly cookieService: CookieService,
        readonly valuesService: ValuesService,
        readonly usefulService: UsefulService,
        readonly ttokenService: TtokenService,
        readonly subscriptionsValuesService: SubscriptionsValuesService,
        readonly commercialIdsService: CommercialidsService,
        private readonly profilesService: ProfilesService
    ) { }

    decideTParam(bundle) {
        if (bundle.type === this.subscriptionsValuesService.type.trial) {
            return this.subscriptionsValuesService.type.trial;
        }else if (bundle.type === this.subscriptionsValuesService.type.free) {
            return this.subscriptionsValuesService.type.free;
        } else if (bundle.status === this.subscriptionsValuesService.status.expired) {
            return this.subscriptionsValuesService.status.expired;
        } else {
            return this.subscriptionsValuesService.type.registered;
        }
    }

    isNew(bundle) {
        return bundle.server_time - bundle.created <= this.valuesService.SECONDS_IN_A_DAY;
    }

    isTrial(bundle) {
        return bundle.type === this.subscriptionsValuesService.type.trial;
    }

    isLicense(bundle) {
        return bundle.service_type === this.subscriptionsValuesService.serviceType.license;
    }

    isGplayOrAppStore(bundle) {
        return !!(bundle.service_type === this.subscriptionsValuesService.serviceType.subscription
                    && (this.subscriptionsService.isGooglePlay ||this.subscriptionsService.isApple(bundle)));
    }

    isSubscription(bundle) {
        return bundle.service_type === this.subscriptionsValuesService.serviceType.subscription;
    }

    isCanceledSubscription(bundle) {
        return this.isSubscription(bundle) && bundle.end_date;
    }

    isEnding(bundle) {
        return bundle.interface.daysRemaining !== undefined
            && bundle.interface.daysRemaining < this.subscriptionsValuesService.monthDays
            && bundle.type !== this.subscriptionsValuesService.type.free
            && (this.isLicense(bundle) || this.isCanceledSubscription(bundle));
    }

    isEndingInDays(bundle) {
        return this.isEnding(bundle)
            && !bundle.processed.arrON
            && bundle.interface.daysRemaining > 0
            && bundle.interface.daysRemaining !== 1;
    }

    isEndingInDay(bundle) {
        return this.isEnding(bundle)
            && !bundle.processed.arrON
            && bundle.interface.daysRemaining === 1;
    }

    canOnlyExtend(bundle) {
        return bundle.bundle_id === this.valuesService.bundleDPI
                || bundle.bundle_id === this.valuesService.bundlePM
                || this.valuesService.idTheftProtectionServices.has(bundle.bundle_id);
    }

    expiringToday(bundle) {
        return this.isEnding(bundle)
            && bundle.interface.expired !== true
            && bundle.interface.daysRemaining === 0;
    }

    expiredDaysAgo(bundle) {
        return this.isEnding(bundle)
            && bundle.interface.expired === true
            && bundle.interface.daysRemaining < 0
            && bundle.interface.daysRemaining < -1;
    }

    expiredDayAgo(bundle) {
        return this.isEnding(bundle)
            && bundle.interface.expired === true
            && bundle.interface.daysRemaining === -1;
    }

    expiredToday(bundle) {
        return this.isEnding(bundle)
            && bundle.interface.expired === true
            && bundle.interface.daysRemaining === 0;
    }

    getSubscriptionsOwner(bundle) {
        const isManagedByOrganization = this.subscriptionsService.isManagedByOrganization(bundle);
        const mspLevelOne = this.profilesService.hasMspOrXspLevelOne();
        if (isManagedByOrganization && !mspLevelOne) {
            return this.profilesService.getOwner();
        }
    }

    computeSlotsAllocationCases(bundle): AllocationCases {
        if (bundle?.slots === this.subscriptionsValuesService.slotsNo.single) {
            return AllocationCases.ONE;
        } else if (bundle?.slots >=  this.subscriptionsValuesService.slotsNo.limit) {
            return AllocationCases.LIMIT_REACHED;
        } else {
            return AllocationCases.MORE_THAN_ONE;
        }
    }

    computeDevicesAllocationCases(bundle: BundleModel): AllocationCases {
        const devicesLength = bundle.interface.devicesNoBox;
        if (devicesLength === this.subscriptionsValuesService.slotsNo.limit) {
            return AllocationCases.LIMIT_REACHED;
        } else if (this.subscriptionsValuesService.slotsNo.intermediate <= devicesLength
                    && devicesLength < this.subscriptionsValuesService.slotsNo.limit) {
            return this.subscriptionsService.isBox(bundle)
                    ? AllocationCases.ABOVE_INTERMIDIATE_BOX
                    : AllocationCases.ABOVE_INTERMIDIATE;
        } else if (devicesLength === this.subscriptionsValuesService.slotsNo.single) {
            return AllocationCases.ONE;
        } else if (devicesLength !== this.subscriptionsValuesService.slotsNo.none
                    && devicesLength < this.subscriptionsValuesService.slotsNo.intermediate
                    && devicesLength !== this.subscriptionsValuesService.slotsNo.single) {
            return AllocationCases.UNDER_INTERMIDIATE;
        } else if (devicesLength === this.subscriptionsValuesService.slotsNo.none) {
            return AllocationCases.ZERO;
        } else {
            return AllocationCases.MORE_THAN_ONE;
        }
    }

    computeAllocationCaseForApplication(devicesLength): AllocationCases {
        if (devicesLength === this.subscriptionsValuesService.slotsNo.limit) {
            return AllocationCases.LIMIT_REACHED;
        } else if (devicesLength === this.subscriptionsValuesService.slotsNo.none) {
            return AllocationCases.ZERO;
        } else if (devicesLength === this.subscriptionsValuesService.slotsNo.single) {
            return AllocationCases.ONE;
        } else if (this.subscriptionsValuesService.slotsNo.intermediate <= devicesLength
                    && devicesLength < this.subscriptionsValuesService.slotsNo.limit) {
            return AllocationCases.ABOVE_INTERMIDIATE;
        } else {
            return AllocationCases.UNDER_INTERMIDIATE;
        }
    }

    getDateFormatted(date: number): string {
        const time = new Date(date * 1000);
        return time.toLocaleDateString();
    }

}
